import React, { useMemo, useEffect } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { ColDef } from 'ag-grid-community'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import { isoToDate } from '../../../../utils/FormatDate'

export type ChartDataPoint = {
  date: string
  vested: number
}

export type NextVestingInfo = {
  nextVestingDate: string | null
  nextVestingOptions: number | null
  nextVestingIncremental: number | null
  totalVested: number
  totalGranted: number
  remainingOptions: number
}

interface VestingDataTableProps {
  data: ChartDataPoint[]
  companyName: string
  onNextVestingChange?: (nextVestingInfo: NextVestingInfo) => void
}

const VestingDataTable: React.FC<VestingDataTableProps> = ({ 
  data, 
  companyName, 
  onNextVestingChange 
}) => {
  const totalGranted = Math.max(...data.map((d) => d.vested))

  // Calculate incremental vesting for each row
  const dataWithIncremental = useMemo(() => {
    return data.map((item, index) => {
      const incremental = index === 0 ? item.vested : item.vested - data[index - 1].vested
      return {
        ...item,
        incrementalVested: incremental,
      }
    })
  }, [data])

  // Calculate next vesting information
  const nextVestingInfo = useMemo<NextVestingInfo>(() => {
    const currentDate = new Date().toISOString().split('T')[0]
    
    // Find the next unvested entry
    const nextVesting = dataWithIncremental.find(item => item.date > currentDate)
    
    // Calculate total currently vested
    const vestedEntries = dataWithIncremental.filter(item => item.date <= currentDate)
    const totalVested = vestedEntries.length > 0 
      ? Math.max(...vestedEntries.map(item => item.vested))
      : 0
    
    const remainingOptions = totalGranted - totalVested

    return {
      nextVestingDate: nextVesting?.date || null,
      nextVestingOptions: nextVesting?.vested || null,
      nextVestingIncremental: nextVesting?.incrementalVested || null,
      totalVested,
      totalGranted,
      remainingOptions
    }
  }, [dataWithIncremental, totalGranted])

  // Send next vesting info to parent component when it changes
  useEffect(() => {
    if (onNextVestingChange) {
      onNextVestingChange(nextVestingInfo)
    }
  }, [nextVestingInfo, onNextVestingChange])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 150,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      filterParams: { buttons: ['reset'], maxNumConditions: 5 },
    }),
    []
  )

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Vesting Date',
        field: 'date',
        valueFormatter: (params: any) => isoToDate(params.value),
        cellStyle: (params: any) => {
          const currentDate = new Date().toISOString().split('T')[0]
          const isVested = params.data.date <= currentDate
          const isNextVesting = params.data.date === nextVestingInfo.nextVestingDate
          return {
            ...tableCellStyle,
            color: isVested ? '#28a745' : '#6c757d',
            fontWeight: isNextVesting ? 'bold' : 'normal',
          }
        },
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Cumulative Vested Options',
        field: 'vested',
        valueFormatter: (params: any) => params.value.toLocaleString() + ' options',
        cellStyle: (params: any) => {
          const currentDate = new Date().toISOString().split('T')[0]
          const isVested = params.data.date <= currentDate
          const isNextVesting = params.data.date === nextVestingInfo.nextVestingDate
          return {
            ...tableCellStyle,
            color: isVested ? '#28a745' : '#6c757d',
            fontWeight: isNextVesting ? 'bold' : 'normal',
          }
        },
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Vested',
        field: 'incrementalVested',
        valueFormatter: (params: any) => params.value.toLocaleString() + ' options',
        cellStyle: (params: any) => {
          const currentDate = new Date().toISOString().split('T')[0]
          const isVested = params.data.date <= currentDate
          const isNextVesting = params.data.date === nextVestingInfo.nextVestingDate
          return {
            ...tableCellStyle,
            color: isVested ? '#28a745' : '#6c757d',
            fontWeight: isNextVesting ? 'bold' : 'normal',
          }
        },
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
    ],
    [nextVestingInfo]
  )

  return (
    <div className='ag-theme-material' style={{ height: '350px', width: '100%' }}>
      <AgGridReact
        sideBar={agConfigSideBar}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={dataWithIncremental}
        animateRows
        pagination={false}
        enableCharts
        enableRangeSelection
        suppressRowTransform
        suppressCopyRowsToClipboard
        suppressCopySingleCellRanges
        suppressCellFocus
        tooltipShowDelay={50}
        tooltipInteraction
        rowClass='border-right-0 border-left-0 border-dashed'
        getRowStyle={(params) => ({
        background: params.rowIndex % 2 === 0 ? '#f8f8f8' : '#ffffff',
        })}
      />
    </div>
  )
}

export default VestingDataTable